import * as React from "react";
import {Form, Formik} from "formik";
import {Card, Typography} from "@mui/material";
import {withStyles} from "@mui/styles";
import InputElement from "../../Common/InputElement";
import {getActiveBooks} from "../../../API/books";

const useStyles = (theme) => ({
    "@global": {
        body: {
            backgroundColor: theme.palette.common.white,
        },
    },
    paper: {
        marginTop: theme.spacing(4),
        padding: theme.spacing(2),
    },
});

class ProductDetailsForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            fieldListData: null,
            books: [],
            booksLoading: false,
            booksError: null,
            objCountry: {list: []},
            objProductTypes: {list: []},
            currencies: [
                {id: "DKK", name: "DKK"},
                {id: "EUR", name: "EUR"},
                {id: "SEK", name: "SEK"},
                {id: "HUF", name: "HUF"},
                {id: "CHF", name: "CHF"},
            ],
            savingBooks: false, // Local state for save loading UX
        };
        this.formRef = React.createRef();
    }

    componentDidMount() {
        this.dataFormationCountries();
        this.dataFormationProductTypes();
        this.fetchBooks();
    }

    fetchBooks = () => {
        this.setState({booksLoading: true, booksError: null});
        getActiveBooks()
            .then((res) => {
                this.setState(
                    {
                        books: (res.data || []).map((book) => ({
                            id: book.book_id.toString(),
                            name: book.title,
                        })),
                        booksLoading: false,
                    },
                    this.updateFieldListData
                );
            })
            .catch((err) => {
                this.setState({
                    booksError: err.message || "Failed to fetch books",
                    booksLoading: false,
                });
            });
    };

    dataFormationProductTypes = () => {
        const {productTypes} = this.props;
        let objProductTypes = {};
        let listProdTypes = [];
        productTypes.main.map((item) => {
            return listProdTypes.push({
                id: item.product_type_id.toString(),
                name: item.product_type_name,
            });
        });
        objProductTypes.list = listProdTypes;
        this.setState({objProductTypes}, this.updateFieldListData);
    };

    dataFormationCountries = () => {
        const {countries} = this.props;
        let objCountry = {};
        let list = [];
        countries.main.map((item) => {
            return list.push({
                id: item.country_id.toString(),
                name: item.country_name,
            });
        });
        objCountry.list = list;
        this.setState({objCountry}, this.updateFieldListData);
    };

    updateFieldListData = () => {
        const {objCountry, objProductTypes, books, currencies} = this.state;
        const fieldList = [
            {
                title: "Product",
                name: "product_name",
                type: "textfield",
            },
            {
                title: "Product External Name",
                name: "product_external_name",
                type: "textfield",
            },
            {
                title: "Product Type",
                name: "product_type_id",
                type: "dropdown",
                options: objProductTypes.list,
            },
            {
                title: "Product Description",
                name: "product_description",
                type: "textfield",
            },
            {
                title: "Price",
                name: "product_price",
                type: "textfield",
            },
            {
                title: "Currency",
                name: "product_price_currency",
                type: "dropdown",
                options: currencies,
            },
            {
                title: "Country",
                name: "product_country",
                type: "dropdown",
                options: objCountry.list,
            },
            {
                title: "Region",
                name: "product_region",
                type: "textfield",
            },
            {
                title: "Parking Info",
                name: "product_parking_info",
                type: "textfield",
            },
            {
                title: "Show on website",
                name: "product_show_on_website",
                type: "checkbox",
            },
            {
                title: "Show in sales",
                name: "product_show_in_sales",
                type: "checkbox",
            },
            {
                title: "Books",
                name: "product_books",
                type: "product_books",
                multiple: true,
                options: books,
            },
        ];
        this.setState({fieldListData: fieldList});
    };

    handleSaveClick = async (_e, name) => {
        const formData = this.formRef.current.values;
        const {productDetails, onClick, onSaveBookAssignments} = this.props;

        if (name === "product_books") {
            if (!productDetails?.product_id) {
                this.setState({
                    booksError: "Product ID is required",
                });
                return;
            }

            this.setState({savingBooks: true});

            try {
                await onSaveBookAssignments(productDetails.product_id, formData.product_books || []);
                this.setState({savingBooks: false, booksError: null});
            } catch (err) {
                this.setState({
                    booksError: err.message || "Failed to sync books",
                    savingBooks: false,
                });
            }
        } else {
            onClick({[name]: formData[name]});
        }
    };

    render() {
        const {classes, productDetails, bookAssignments, bookAssignmentsLoading, bookAssignmentsError} = this.props;
        const {fieldListData, booksLoading, booksError, savingBooks} = this.state;
        const initialFormData = {
            ...productDetails,
            product_show_in_sales: !!productDetails.product_show_in_sales,
            product_show_on_website: !!productDetails.product_show_on_website,
            product_books: bookAssignments || [],
        };

        return (
            <React.Fragment>
                {productDetails && fieldListData && (
                    <Card className={classes.paper}>
                        <Typography component="h6" variant="h6" color="textPrimary" gutterBottom>
                            {productDetails.product_external_name}
                        </Typography>
                        {booksError && (
                            <Typography color="error" style={{marginBottom: "10px"}}>
                                {booksError}
                            </Typography>
                        )}
                        {booksLoading && (
                            <Typography style={{marginBottom: "10px"}}>
                                Loading books...
                            </Typography>
                        )}
                        {bookAssignmentsError && (
                            <Typography color="error" style={{marginBottom: "10px"}}>
                                {bookAssignmentsError}
                            </Typography>
                        )}
                        {bookAssignmentsLoading && (
                            <Typography style={{marginBottom: "10px"}}>
                                Loading book assignments...
                            </Typography>
                        )}
                        {savingBooks && (
                            <Typography style={{marginBottom: "10px"}}>
                                Saving book assignments...
                            </Typography>
                        )}
                        <Formik
                            innerRef={this.formRef}
                            initialValues={initialFormData}
                            enableReinitialize={true}
                            validate={(values) => {
                                const errors = {};
                                if (values.product_books && !Array.isArray(values.product_books)) {
                                    errors.product_books = "Books must be an array of IDs";
                                }
                                return errors;
                            }}
                            onSubmit={(_values, {setSubmitting}) => {
                                setSubmitting(false);
                                // Form submission not used, as Save buttons handle individual fields
                            }}
                            render={({_submitForm, _isSubmitting, values, setFieldValue, errors, touched}) => (
                                <Form>
                                    {fieldListData.map((item, index) => (
                                        <InputElement
                                            {...item}
                                            key={index}
                                            onSaveClick={this.handleSaveClick}
                                            value={values[item.name]}
                                            setFieldValue={setFieldValue}
                                            error={touched[item.name] && errors[item.name]}
                                            disabled={item.name === "product_books" && savingBooks}
                                        />
                                    ))}
                                </Form>
                            )}
                        />
                    </Card>
                )}
            </React.Fragment>
        );
    }
}

export default withStyles(useStyles)(ProductDetailsForm);